import { useProfile } from '@hooks/use-profile';
import { Typography } from '@julo-ui/react';
import { NT80, NT90 } from '@julofinance/color-token';
import formatMoney from '@julofinance/web-helpers/dist/string/formatMoney';
import { useRGetSelectedLoan } from '@repositories/loan';
import ApplicationCard from 'pages/Home/NewApplication/components/application-card';
import SectionTitle from 'pages/Home/NewApplication/components/section-title';
import { useHistory } from 'react-router-dom';
import { loanWrapperCx } from './styles';
import utils from 'utils';
import MissingInputValidation from '../../dialogs/missing-input-validation';
import { useState } from 'react';
import {
  FormParam,
  SavedApplicationDataResponse,
  useRSaveApplication,
  useRVerifyBankAccount,
} from '@repositories/new-application';
import { useSnackbar } from '@new-components/elements';
import { setErrors, excludeKey } from '@utils/object';
import useGlobalState from 'actions';
import { useActionLog } from '@hooks/use-action-log';
import { useFormContext } from '@hooks/react-hook-form';
import DialogBankAccountValidation from '../../dialogs/bank-account-validation';
import DialogFailedBankAccountValidation from '../../dialogs/failed-bank-account-verification';

const PreLoan = ({ readonly = false }: { readonly?: boolean }) => {
  const history = useHistory();
  const { data: profile } = useProfile();
  const { data: preLoanRes } = useRGetSelectedLoan();
  const { onOpen } = useSnackbar();
  const [, actions] = useGlobalState();
  const preLoanData = preLoanRes?.data;
  const { pushEvent } = useActionLog();

  const [showDialog, setShowDialog] = useState(false);
  const [showBankValidationModal, setShowBankValidationModal] = useState(false);
  const [showFailedBankValidationModal, setShowFailedBankValidationModal] =
    useState(false);

  const { setValue, getValues, trigger, setError } = useFormContext();

  const { mutate: mutateSaveForm } = useRSaveApplication({
    onSuccess: () => {
      utils.store.set('preAuthToken', profile?.grabToken || '');
      localStorage.setItem('origin', 'app_form');
      if (preLoanData?.loanOffer) {
        localStorage.setItem(
          'pre_loan_offer',
          JSON.stringify({
            ...preLoanData?.loanOffer,
            interestRate: preLoanData?.repayment?.interestRate,
            upfrontFee: preLoanData?.loan?.fee,
            loanAmount: preLoanData?.loan.amount,
            repaymentTenure: preLoanData?.repayment.tenure,
          }),
        );
      }
      history.push('/offer');
    },
    onError: (err) => {
      const statusCode = err.payload?.statusCode;
      const errors = (err.payload?.errors || {}) as FormParam<'financial'>;

      switch (statusCode) {
        case 400:
          setErrors<FormParam<'financial'>>(errors, setError);
          break;

        default:
          onOpen({
            message:
              (err.payload?.errors as Array<string>)?.[0] ||
              'Opps, Something went wrong.',
            severity: 'error',
          });
          break;
      }
    },
    onSettled: () => {
      actions.closeLoadingOverlay();
    },
  });

  const { mutate: verifyBankAccount, isLoading: isLoadingVerifyBankAccount } =
    useRVerifyBankAccount({
      onSuccess: () => {
        pushEvent({
          element: 'success_bank_account_verification',
          module: 'app_form_financial_page',
        });
        setShowBankValidationModal(false);
        const fields = getValues();
        mutateSaveForm(
          {
            variables: {
              ...excludeKey<SavedApplicationDataResponse<'financial'>>(
                fields as SavedApplicationDataResponse<'financial'>,
                ['currentStep'],
              ),
              step: 3,
            },
          },
          {
            onSuccess: () => {
              setValue('bankAccountNumberVerified', true);
              setShowBankValidationModal(false);
            },
          },
        );
      },
      onError: (err) => {
        const payload = (err.payload || {
          statusCode: 400,
          errors: [],
        }) as {
          statusCode: number;
          errors: string[];
        };
        const statusCode = payload.statusCode;
        setValue('bankAccountNumberVerified', false);
        switch (statusCode) {
          case 400:
            setShowBankValidationModal(false);
            setShowFailedBankValidationModal(true);
            setError('bankAccountNumber', {
              message: payload.errors[0],
            });
            pushEvent({
              element: 'failed_bank_account_verification',
              module: 'app_form_financial_page',
            });
            break;

          default:
            pushEvent({
              element: 'error_bank_account_verification',
            });
            break;
        }
      },
    });

  const handleVerify = () => {
    verifyBankAccount({
      variables: {
        bankAccountNumber: getValues('bankAccountNumber') || '',
        bankName: getValues('bankName') || '',
      },
    });
  };

  const handleChangeLoan = async () => {
    const isValid = await trigger([
      'bankAccountNumber',
      'bankName',
      'loanPurpose',
      'monthlyIncome',
    ]);
    if (isValid) {
      pushEvent({
        element: 'user_change_loan_offer',
        event: 'click',
        module: 'app_form_financial_page',
      });
      setShowBankValidationModal(true);
    } else {
      setShowDialog(true);
    }
  };

  return (
    <ApplicationCard>
      {!readonly && <SectionTitle title='Pinjaman yang Dipilih' />}

      <div className={loanWrapperCx}>
        <div>
          <Typography type='body' color={NT80}>
            Jumlah Pinjaman
          </Typography>
          <Typography type='body' bold color={NT90}>
            {formatMoney(preLoanData?.loan.amount || 0)}
          </Typography>
        </div>
        <div>
          <Typography type='body' color={NT80}>
            Jangka waktu
          </Typography>
          <Typography type='body' bold color={NT90}>
            {formatMoney(preLoanData?.repayment.tenure || 0, false)} hari
          </Typography>
        </div>
        {!readonly && (
          <span className='btn-change' onClick={handleChangeLoan}>
            Ubah
          </span>
        )}
      </div>

      <MissingInputValidation
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
      />

      <DialogBankAccountValidation
        isOpen={showBankValidationModal}
        onCancel={() => {
          setValue('bankAccountNumberVerified', false);
          setShowBankValidationModal(false);
        }}
        onVerify={handleVerify}
        isLoading={isLoadingVerifyBankAccount}
      />

      <DialogFailedBankAccountValidation
        isOpen={showFailedBankValidationModal}
        onClose={() => setShowFailedBankValidationModal(false)}
      />
    </ApplicationCard>
  );
};

export default PreLoan;
